import React, { useState, useRef, useCallback, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

const ImageLightbox = ({ images = [], initialIndex = 0, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [translateX, setTranslateX] = useState(0);
  const [translateY, setTranslateY] = useState(0);
  const [opacity, setOpacity] = useState(1);
  const [transitioning, setTransitioning] = useState(false);
  const touchStart = useRef({ x: 0, y: 0 });
  const touchDelta = useRef({ x: 0, y: 0 });
  const isDragging = useRef(false);
  const dragAxis = useRef(null); // 'x' or 'y' — locks after 10px threshold
  const containerRef = useRef(null);

  const total = images.length;

  // Prevent body scroll while lightbox is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  // Keyboard navigation
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft' && currentIndex > 0) goTo(currentIndex - 1);
      if (e.key === 'ArrowRight' && currentIndex < total - 1) goTo(currentIndex + 1);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [currentIndex, total]);

  const goTo = useCallback((idx) => {
    setTransitioning(true);
    setTranslateX(0);
    setCurrentIndex(idx);
    setTimeout(() => setTransitioning(false), 300);
  }, []);

  const handleTouchStart = (e) => {
    const touch = e.touches[0];
    touchStart.current = { x: touch.clientX, y: touch.clientY };
    touchDelta.current = { x: 0, y: 0 };
    isDragging.current = true;
    dragAxis.current = null;
    setTransitioning(false);
  };

  const handleTouchMove = (e) => {
    if (!isDragging.current) return;
    const touch = e.touches[0];
    const dx = touch.clientX - touchStart.current.x;
    const dy = touch.clientY - touchStart.current.y;
    touchDelta.current = { x: dx, y: dy };

    // Lock axis after 10px threshold
    if (!dragAxis.current) {
      if (Math.abs(dx) > 10 || Math.abs(dy) > 10) {
        dragAxis.current = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
      } else return;
    }

    if (dragAxis.current === 'x') {
      // Horizontal swipe — gallery nav
      // Add resistance at edges
      let finalDx = dx;
      if ((currentIndex === 0 && dx > 0) || (currentIndex === total - 1 && dx < 0)) {
        finalDx = dx * 0.3;
      }
      setTranslateX(finalDx);
    } else {
      // Vertical swipe — dismiss
      if (dy > 0) {
        setTranslateY(dy);
        setOpacity(Math.max(0, 1 - dy / 300));
      }
    }
  };

  const handleTouchEnd = () => {
    if (!isDragging.current) return;
    isDragging.current = false;
    const { x: dx, y: dy } = touchDelta.current;

    if (dragAxis.current === 'x') {
      setTransitioning(true);
      const threshold = 60;
      if (dx < -threshold && currentIndex < total - 1) {
        goTo(currentIndex + 1);
      } else if (dx > threshold && currentIndex > 0) {
        goTo(currentIndex - 1);
      } else {
        setTranslateX(0);
        setTimeout(() => setTransitioning(false), 300);
      }
    } else if (dragAxis.current === 'y') {
      if (dy > 100) {
        onClose();
      } else {
        setTransitioning(true);
        setTranslateY(0);
        setOpacity(1);
        setTimeout(() => setTransitioning(false), 300);
      }
    }

    dragAxis.current = null;
  };

  const handleBackdropClick = (e) => {
    if (e.target === containerRef.current || e.target.dataset.backdrop) {
      onClose();
    }
  };

  return (
    <div
      ref={containerRef}
      className="fixed inset-0 z-[100] flex items-center justify-center select-none"
      style={{ backgroundColor: `rgba(0,0,0,${Math.max(0.3, opacity)})` }}
      onClick={handleBackdropClick}
      data-testid="image-lightbox"
    >
      {/* Close button — top right */}
      <button
        className="absolute top-4 right-4 z-[110] h-11 w-11 flex items-center justify-center rounded-full bg-white/15 backdrop-blur-sm hover:bg-white/25 transition-colors"
        onClick={onClose}
        data-testid="lightbox-close-btn"
        style={{ WebkitTapHighlightColor: 'transparent' }}
      >
        <X className="h-5 w-5 text-white" />
      </button>

      {/* Image counter */}
      {total > 1 && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-[110] text-white/70 text-[13px] font-medium tracking-wide" data-testid="lightbox-counter">
          {currentIndex + 1} / {total}
        </div>
      )}

      {/* Desktop arrow nav */}
      {total > 1 && currentIndex > 0 && (
        <button
          className="absolute left-3 top-1/2 -translate-y-1/2 z-[110] hidden md:flex h-10 w-10 items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors"
          onClick={() => goTo(currentIndex - 1)}
          data-testid="lightbox-prev-btn"
        >
          <ChevronLeft className="h-5 w-5 text-white" />
        </button>
      )}
      {total > 1 && currentIndex < total - 1 && (
        <button
          className="absolute right-3 top-1/2 -translate-y-1/2 z-[110] hidden md:flex h-10 w-10 items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors"
          onClick={() => goTo(currentIndex + 1)}
          data-testid="lightbox-next-btn"
        >
          <ChevronRight className="h-5 w-5 text-white" />
        </button>
      )}

      {/* Image area with touch handling */}
      <div
        className="w-full h-full flex items-center justify-center px-4"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        data-backdrop="true"
      >
        <img
          src={images[currentIndex]}
          alt={`Image ${currentIndex + 1} of ${total}`}
          className="max-w-full max-h-[85vh] object-contain rounded-sm pointer-events-none"
          style={{
            transform: `translateX(${translateX}px) translateY(${translateY}px)`,
            transition: transitioning ? 'transform 0.3s ease-out' : 'none',
            userSelect: 'none',
            WebkitUserDrag: 'none',
          }}
          draggable={false}
          data-testid="lightbox-image"
        />
      </div>

      {/* Dot indicators for mobile */}
      {total > 1 && total <= 12 && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-[110] flex gap-1.5" data-testid="lightbox-dots">
          {images.map((_, i) => (
            <div
              key={i}
              className={`h-1.5 rounded-full transition-all duration-300 ${i === currentIndex ? 'w-4 bg-white' : 'w-1.5 bg-white/40'}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ImageLightbox;
